import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { FormBuilder, Validators } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatProgressBar } from '@angular/material/progress-bar'
import {
  AppointmentModel,
  AppService,
  AuthService,
  ClinicService,
  CrudService,
  InsuranceProviderLookup,
  Patient,
  PatientInsurance,
  PatientInsuranceStatus,
  PatientInsuranceType,
} from '@mmx/shared'
import Fuse from 'fuse.js'
import moment from 'moment-timezone'
import {
  filter,
  map,
  Observable,
  startWith,
  Subject,
  takeUntil,
  tap,
} from 'rxjs'

@Component({
  selector: 'mmx-insurance-dialog',
  templateUrl: './insurance-dialog.component.html',
  standalone: false,
})
export class InsuranceDialogComponent implements OnInit, OnDestroy {
  @ViewChild(MatProgressBar, { static: true }) progressBar: MatProgressBar

  public PATIENT_INSURANCE_TYPE = PatientInsuranceType
  public PATIENT_INSURANCE_STATUS = PatientInsuranceStatus

  public form = this.fb.group({
    type: this.fb.control<PatientInsuranceType>(PatientInsuranceType.PRIMARY),
    lookup: this.fb.control<InsuranceProviderLookup>(
      { value: null, disabled: true },
      Validators.required,
    ),
    memberId: this.fb.control<string>(null),
    groupNumber: this.fb.control<string>(null),
    groupName: this.fb.control<string>(null),
    effectiveFrom: this.fb.control<moment.Moment>(null),
    effectiveTo: this.fb.control<moment.Moment>(null),
    status: this.fb.control<PatientInsuranceStatus>(
      PatientInsuranceStatus.NotVerified,
    ),
    conflictResolution: this.fb.control<'inactivate' | 'nothing'>('inactivate'),
  })
  public carriers: InsuranceProviderLookup[] = []
  public filteredProviderLookups$: Observable<InsuranceProviderLookup[]>
  public popularInsurances: InsuranceProviderLookup[]
  public hasExistingCarrier = false
  public isSaving = false
  public isLoading = false
  public lastVerifiedAt?: moment.Moment
  public lastVerifiedBy?: string

  private fuse: Fuse<InsuranceProviderLookup>
  private onDestroy$ = new Subject<void>()

  get timezone(): string {
    return (
      this.data.appointment?.timezone ??
      this.clinicService.clinic.settings.defaultTimezone
    )
  }

  get existingCarrier(): InsuranceProviderLookup | undefined {
    if (this.data.insurance) {
      return this.carriers.find((carrier) => {
        return carrier.id === this.data.insurance.carrierLookupId
      })
    }
  }

  get notes(): string | undefined {
    return (this.form.value.lookup ?? this.existingCarrier)?.notes
  }

  private conflictingInsurance?: PatientInsurance

  hasAnotherActiveInsuranceWithSameType$: Observable<
    PatientInsurance | undefined
  > = this.form.valueChanges.pipe(
      startWith(this.form.value),
      takeUntil(this.onDestroy$),
      filter(() => this.data.insurances?.length > 0),
      map(() => {
        if (this.form.value.status !== PatientInsuranceStatus.Inactive) {
          return this.data.insurances.find(
            (insurance) =>
              insurance.type === this.form.value.type &&
            insurance.status !== PatientInsuranceStatus.Inactive,
          )
        }
      }),
      tap((insurance) => (this.conflictingInsurance = insurance)),
    )

  constructor(
    public dialogRef: MatDialogRef<InsuranceDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    private data: {
      patient: Patient
      appointment: AppointmentModel
      insurance?: PatientInsurance
      insurances?: PatientInsurance[]
    },
    public clinicService: ClinicService,
    public appService: AppService,
    private crudService: CrudService,
    private authService: AuthService,
    private fb: FormBuilder,
  ) {}

  ngOnInit() {
    if (this.data.insurance) {
      const insurance = this.data.insurance

      this.form.patchValue({
        type: insurance.type,
        memberId: insurance.memberId,
        groupNumber: insurance.groupNumber,
        groupName: insurance.groupName,
        effectiveFrom: insurance.effectiveFrom
          ? moment.utc(insurance.effectiveFrom)
          : undefined,
        effectiveTo: insurance.effectiveTo
          ? moment.utc(insurance.effectiveTo)
          : undefined,
        status: insurance.status || PatientInsuranceStatus.NotVerified,
      })

      this.form.updateValueAndValidity()

      this.setCarriers([
        new InsuranceProviderLookup({
          id: insurance.carrierLookupId,
          clinicCarrierId: insurance.clinicCarrierId,
          clinicCarrierName: insurance.clinicCarrierName,
        }),
      ])

      this.hasExistingCarrier = true
      this.form.controls.lookup.setValue(this.existingCarrier)
      this.form.controls.lookup.updateValueAndValidity()
      this.form.controls.lookup.disable()

      this.lastVerifiedAt = insurance.lastVerifiedAt
      this.lastVerifiedBy = insurance.lastVerifiedBy
    } else {
      this.loadCarriers()
    }

    this.filteredProviderLookups$ = this.form.controls.lookup.valueChanges.pipe(
      takeUntil(this.onDestroy$),
      startWith(''),
      map((value) => this.filterAutocomplete(value)),
    )

    this.form.controls.status.valueChanges
      .pipe(
        takeUntil(this.onDestroy$),
        tap((status) => {
          if (status === PatientInsuranceStatus.Active) {
            this.updateLastVerifiedAt()
          } else {
            this.lastVerifiedAt = this.data.insurance?.lastVerifiedAt
          }
        }),
      )
      .subscribe()
  }

  hasActiveInsurance(type: PatientInsuranceType) {
    return this.data.insurances?.find(
      (insurance) =>
        insurance.status !== PatientInsuranceStatus.Inactive &&
        insurance.type === type,
    )
  }

  private loadCarriers() {
    this.isLoading = true
    this.progressBar.mode = 'indeterminate'

    this.crudService.index(InsuranceProviderLookup).subscribe((carriers) => {
      this.setCarriers(carriers)
      this.progressBar.mode = 'determinate'
      this.isLoading = false

      // default to triggering filtered carriers observable
      this.form.controls.lookup.setValue(null)
      this.form.controls.lookup.updateValueAndValidity()
    })
  }

  private setCarriers(carriers: InsuranceProviderLookup[]) {
    this.carriers = carriers.filter((c) => c.status !== 'inactive')
    this.popularInsurances = this.carriers.filter((c) => c.popular)
    // const carrierValues = this.carriers.map((carrier) => carrier.id)

    this.fuse = new Fuse(this.carriers, {
      keys: ['viewValue'],
      shouldSort: true,
    })
    this.form.controls.lookup.enable()

    // if (this.form) {
    //   this.form.controls.lookup.setValidators([
    //     Validators.required,
    //     oneOfValidator(carrierValues),
    //   ])
    // }
  }

  private filterAutocomplete(
    val: string | InsuranceProviderLookup,
  ): InsuranceProviderLookup[] {
    if (val) {
      if (typeof val === 'string') {
        const results =
          this.fuse?.search(val).map((result) => result.item) || []
        const exactMatches = results.filter(
          (lookup) => lookup.viewValue === val,
        )
        if (
          exactMatches.length === 1 &&
          exactMatches[0].viewValue?.toLowerCase() === val.toLowerCase()
        ) {
          this.form.controls.lookup.setValue(exactMatches[0])
          this.form.controls.lookup.updateValueAndValidity()
        }

        return results
      } else {
        return [val]
      }
    } else if (this.popularInsurances?.length > 0) {
      // Show popular insurances on empty input search
      return this.popularInsurances
    } else {
      return this.carriers
    }
  }

  ngOnDestroy(): void {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  onSubmit() {
    if (this.isSaving || this.progressBar.mode == 'indeterminate') {
      return
    }

    const insurance = this.data.insurance ?? new PatientInsurance()

    if (this.hasExistingCarrier) {
      insurance.carrierLookupId = this.data.insurance.carrierLookupId
      insurance.clinicCarrierId = this.data.insurance.clinicCarrierId
      insurance.clinicCarrierName = this.data.insurance.clinicCarrierName
    } else if (this.form.value.lookup) {
      const carrier: InsuranceProviderLookup = this.form.value.lookup
      insurance.carrierLookupId = carrier.id
      insurance.clinicCarrierId = carrier.clinicCarrierId
      insurance.clinicCarrierName = carrier.clinicCarrierName
    }

    insurance.type = this.form.value.type
    insurance.patientId = this.data.patient.id
    insurance.memberId = this.form.value.memberId
    insurance.groupNumber = this.form.value.groupNumber
    insurance.groupName = this.form.value.groupName
    insurance.status = this.form.value.status
    insurance.effectiveFrom = this.form.value.effectiveFrom
      ? this.form.value.effectiveFrom.format('YYYY-MM-DD')
      : undefined
    insurance.effectiveTo = this.form.value.effectiveTo
      ? this.form.value.effectiveTo.format('YYYY-MM-DD')
      : undefined
    insurance.lastVerifiedAt = this.lastVerifiedAt
    insurance.lastVerifiedBy = this.lastVerifiedBy

    this.isSaving = true
    this.progressBar.mode = 'indeterminate'

    const query = {
      appointmentId: this.data.appointment?.id,
      conflictInsuranceId: this.conflictingInsurance?.id,
      conflictResolution: this.form.value.conflictResolution,
    }

    this.crudService
      .save(insurance, query)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response) => {
          this.isSaving = false
          this.progressBar.mode = 'determinate'
          const ins = new PatientInsurance(response)

          if (this.data.appointment) {
            this.data.appointment.upsertInsurance(ins)
          }

          this.dialogRef.close(ins)
        },
        error: () => {
          alert(
            'An error occurred while trying to save the patient insurance policy',
          )
          this.isSaving = false
          this.progressBar.mode = 'determinate'
        },
      })
  }

  updateLastVerifiedAt() {
    this.lastVerifiedAt = moment()
    this.lastVerifiedBy =
      this.authService.user.abbreviation ?? this.authService.user.name
  }

  displayCarrierAutocompleteLabel(carrier?: InsuranceProviderLookup): string {
    return carrier?.viewValue || ''
  }

  getErrorMessage(controlName: string) {
    const control = this.form.get(controlName)

    if (control) {
      if (control.hasError('required')) {
        return 'Field is required'
      } else if (control.hasError('minDate')) {
        return 'Date cannot be before today'
      } else if (control.hasError('startsBeforeEnd')) {
        return 'Start must be before end'
      }
    }
  }
}
